import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { ROUTES } from '@/constants/routes';
import { Alert } from '@/design-system/components/Alert';
import { Loader } from '@/design-system/components/Loader';
import { toAppError } from '@/domain/errors';
import { AuthenticationLayout } from '@/layouts/AuthenticationLayout';
import { authenticationService } from '@/services/authentication-service';

function VerifyEmailPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: () => authenticationService.verifyEmailChange(token),
    onSuccess: () => {
      void navigate({ to: ROUTES.signIn, replace: true });
    },
  });

  useEffect(() => {
    mutation.mutate();
    // Redeem once on mount — the token is single-use.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional single redeem
  }, [token]);

  const error = mutation.error !== null ? toAppError(mutation.error) : null;

  return (
    <AuthenticationLayout
      title="Verify Email"
      description="Confirming your new administrator email address."
    >
      {mutation.isPending ? <Loader className="py-8" /> : null}
      {mutation.isSuccess ? (
        <Alert severity="success" title="Email updated">
          Sign in again with your new email address.
        </Alert>
      ) : null}
      {error !== null ? (
        <div className="space-y-4">
          <Alert severity="error" title={error.title}>
            {error.message}
          </Alert>
          <p className="text-sm text-muted-foreground">
            Edit your email and save again from the console to receive a new link, then{' '}
            <Link to={ROUTES.signIn} className="underline">
              sign in
            </Link>
            .
          </p>
        </div>
      ) : null}
    </AuthenticationLayout>
  );
}

export const Route = createFileRoute('/admin/verify-email/$token')({
  component: VerifyEmailPage,
});
